import { HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Image,
  Link,
  Spacer,
  Text,
  useDisclosure,
  useMediaQuery,
} from "@chakra-ui/react";
import * as React from "react";
import { MutableRefObject } from "react";
import { useNavigate } from "react-router-dom";

import logo from "@/assets/monarch-logo-only.png";
import logoWhite from "@/assets/monarch-logo-white.png";
import { Avatar, useGetAvatarQuery, useGetUserQuery } from "@/features/users";

import { Footer } from "./Footer";
import { Sidebar } from "./Sidebar";

type MainProps = {
  children: React.ReactNode;
};

export const MainLayout = ({ children }: MainProps) => {
  const [isLargerThan720] = useMediaQuery("(min-width: 720px)");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();
  useGetUserQuery();
  useGetAvatarQuery();

  return (
    <>
      <Flex flexDirection="column" minHeight="100vh">
        <Box position="sticky" top={0} zIndex={10}>
          {import.meta.env.VITE_ENVIRONMENT !== "production" && (
            <Flex
              bgColor="darkred"
              color="white"
              justifyContent="center"
              textAlign="center"
              paddingY={3}
            >
              <Text as="b" fontSize="xl">
                This is a sandbox environment! Anything submitted will not count
                towards successful audits. The production environment can be
                found{" "}
                <Link
                  textDecoration="underline"
                  href="https://audits.staffaccess.net"
                >
                  here
                </Link>
              </Text>
            </Flex>
          )}
          {isLargerThan720 ? (
            <DesktopLayout onOpen={onOpen} btnRef={btnRef} />
          ) : (
            <MobileLayout onOpen={onOpen} btnRef={btnRef} />
          )}
        </Box>
        <Sidebar isOpen={isOpen} onClose={onClose} btnRef={btnRef} />

        <Flex flexGrow={1} justifyContent="center">
          <Flex
            marginTop={5}
            maxWidth={isLargerThan720 ? "1600px" : "100vw"}
            width="100%"
            alignItems="flex-start"
          >
            <Box paddingX="2rem" width="full">
              {children}
            </Box>
          </Flex>
        </Flex>
        {isLargerThan720 && <Footer />}
      </Flex>
    </>
  );
};

type NavbarProps = {
  onOpen: () => void;
  btnRef: MutableRefObject<any>;
};

const MobileLayout = ({ onOpen, btnRef }: NavbarProps) => {
  return (
    <Box w="100vw" backgroundColor="brand.darkblue" padding={2}>
      <Box position="relative" h="2rem" textAlign="center" padding={0}>
        <Box
          maxW="3rem"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
        >
          <Image src={logo} width="100%" alt="Monarch logo" />
        </Box>
        <Box
          maxW="3rem"
          position="absolute"
          top="50%"
          right="-15px"
          transform="translate(-50%, -50%)"
        >
          <IconButton
            aria-label="Show sidebar"
            icon={<HamburgerIcon />}
            bg="transparent"
            color="white"
            fontSize="3rem"
            ref={btnRef}
            _active={{
              bg: "transparent",
            }}
            _focus={{
              boxShadow: "none",
            }}
            onClick={onOpen}
          ></IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const DesktopLayout = ({ onOpen, btnRef }: NavbarProps) => {
  const navigate = useNavigate();
  return (
    <>
      <Box w="100%" maxHeight="6rem" bg="brand.darkblue" marginBottom="3rem">
        <Flex
          alignItems="flex-end"
          justifyContent="space-around"
          maxW="1600px"
          margin="0 auto"
          p={4}
        >
          <Box
            maxW="250px"
            m={1}
            onClick={() => navigate("/")}
            cursor="pointer"
          >
            <Image src={logoWhite} w="100%" alt="Monarch logo" />
          </Box>
          <Spacer />
          <Heading as="h1" size="xl" fontWeight="bold" color="white">
            {import.meta.env.VITE_ENVIRONMENT === "production"
              ? "Site Audits"
              : "Site Audits Sandbox"}
          </Heading>
          <Box w="fit-content" m={1.5} ml={6}>
            <Avatar
              width="3rem"
              height="3rem"
              handleClick={onOpen}
              btnRef={btnRef}
            />
          </Box>
        </Flex>
      </Box>
    </>
  );
};
